import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import ShrinkWrapPanel from "@/components/ui/ShrinkWrapPanel";
import { usePreloadImages } from "@/lib/usePreloadImages";

const DEFAULT_SLIDES = [
  {
    image: "https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=1400&q=80",
    headline: "Tee Off in Paradise",
    sub: "World-class golf courses at your fingertips",
  },
  {
    image: "https://images.unsplash.com/photo-1500932334442-8761ee4810a7?w=1400&q=80",
    headline: "Feel the Fairway",
    sub: "Exclusive tee times — book in seconds",
  },
  {
    image: "https://images.unsplash.com/photo-1611374243147-44a702c2d44c?w=1400&q=80",
    headline: "Island Golf Getaways",
    sub: "Ferry, stay & play — all in one booking",
  },
  {
    image: "https://images.unsplash.com/photo-1593111774240-d529f12cf4bb?w=1400&q=80",
    headline: "Premium Experiences",
    sub: "Spa, coaching & luxury stays included",
  },
];

const AUTO_PLAY_MS = 5000;

export default function MastheadBanner({ fields = {} }) {
  const slides = fields.slides?.length ? fields.slides : DEFAULT_SLIDES;
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);
  const touchStartX = useRef(null);

  const count = slides.length;

  const goTo = (idx) => setCurrent((idx + count) % count);
  const prev = () => goTo(current - 1);
  const next = () => goTo(current + 1);

  useEffect(() => {
    if (paused) return;
        timerRef.current = setTimeout(() => next(), AUTO_PLAY_MS);
    return () => clearTimeout(timerRef.current);
  }, [current, paused]);

  usePreloadImages(slides.map((s) => s.image));

  const slide = slides[current];

  return (
    <div
      className="relative w-full overflow-hidden"
      style={{ height: "clamp(340px, 52vw, 540px)" }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => (touchStartX.current = e.touches[0].clientX)}
      onTouchEnd={(e) => {
        // Arrows are hidden on mobile, so swipe left/right to change slides
        if (count < 2 || touchStartX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        touchStartX.current = null;
        if (Math.abs(dx) > 40) (dx < 0 ? next : prev)();
      }}
    >
      {/* Media layer */}
      <AnimatePresence mode="sync">
        <motion.div
          key={current}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7 }}
          className="absolute inset-0"
        >
          <img src={slide.image} alt={slide.headline} className="w-full h-full object-cover" />
          {/* Dark gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/70" />
        </motion.div>
      </AnimatePresence>

      {/* Text content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`text-${current}`}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="absolute inset-0 flex items-end pointer-events-none"
        >
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
            {/* Frosted dark panel keeps copy legible on bright photos — matches ExquraHero */}
            <ShrinkWrapPanel className="w-fit max-w-2xl border border-white/10 bg-black/40 px-6 py-6 sm:px-8 sm:py-7 shadow-[0_8px_32px_rgba(0,0,0,0.25)] backdrop-blur-md">
              <p className="text-[#7ccfbd] text-sm sm:text-base font-semibold uppercase mb-4">
                EXQURA · Golf & Experiences
              </p>
              <h1 className="text-white font-bold text-4xl sm:text-6xl lg:text-7xl leading-tight mb-3">
                {slide.headline}
              </h1>
              <p className="text-white/85 text-base sm:text-lg">{slide.sub}</p>
            </ShrinkWrapPanel>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Prev / Next arrows */}
      <button
        onClick={prev}
        className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-black/30 hover:bg-black/50 hidden lg:flex items-center justify-center text-white backdrop-blur-sm transition-colors"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        onClick={next}
        className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-black/30 hover:bg-black/50 hidden lg:flex items-center justify-center text-white backdrop-blur-sm transition-colors"
      >
        <ChevronRight className="h-5 w-5" />
      </button>

      {/* Dot indicators */}
      <div className="absolute bottom-6 right-4 sm:right-8 z-20 flex gap-2">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`rounded-full transition-all duration-300 ${
              i === current ? "w-6 h-2 bg-white" : "w-2 h-2 bg-white/50 hover:bg-white/80"
            }`}
          />
        ))}
      </div>

      {/* Progress bar */}
      {!paused && (
        <motion.div
          key={`progress-${current}`}
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: AUTO_PLAY_MS / 1000, ease: "linear" }}
          className="absolute bottom-0 left-0 h-0.5 bg-blue-400 origin-left w-full z-20"
        />
      )}
    </div>
  );
}
